// src/components/RegistroInterventi.js
// ═══════════════════════════════════════════════════════════════════════════
// REGISTRO INTERVENTI PER CLIENTE (ottobre 2026) — un solo registro, due ingressi
// ═══════════════════════════════════════════════════════════════════════════
// • Gestione → Registri → Registro interventi: si sceglie il cliente, poi il suo registro.
// • Scheda «Registro interventi» della commessa: lo stesso registro, già filtrato sulla commessa.
// Si compila da solo dai rapportini. Ogni intervento è citato con data e numero del rapportino
// (INT-AAAA-NNNN), in ordine cronologico; il numero apre il rapportino vero.
// Il cliente si riconosce dalla commessa (anagrafica clienti), non dal nome scritto a mano nel
// rapportino: «Atl» e «ATL» finiscono nello stesso registro. Solo se la commessa non ha un
// cliente si usa il nome scritto nel rapportino.
// Stampa: carta intestata (utils/cartaIntestata.js), A4 orizzontale, con o senza ore e tecnici.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { FileText, Search, Printer, ChevronLeft } from "lucide-react";
import { apriDocumentoRapportino, STATI_INTERVENTO } from "./RapportiniTab";
import { descrizioneSede } from "../utils/sediCliente";
import { esc, testataHtml, piedeHtml, apriDocumento, datiAzienda } from "../utils/cartaIntestata";
const datiNomeImpresa = org => datiAzienda(org).nome || "l'impresa";
import { esportaXLSX } from "../utils/esportaXLSX";

const TIPOLOGIE = { ordinaria: "Ordinaria", straordinaria: "Straordinaria", predittiva: "Predittiva", urgenza: "Urgenza", manutenzione: "Manutenzione" };
const dataIt = iso => iso ? new Date(String(iso).slice(0, 10) + "T00:00:00").toLocaleDateString("it-IT") : "—";
const tronca = (t, n) => { const s = String(t || "").trim(); return s.length > n ? s.slice(0, n).trim() + "…" : s; };
const fmtOre = n => (Math.round(n * 100) / 100).toLocaleString("it-IT");
const esitoTesto = k => (STATI_INTERVENTO[k]?.label || "").replace(/^[^\p{L}]+/u, "");
const normalizza = s => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

// Ore di ogni operatore: quelle scritte accanto al nome, altrimenti la durata dell'intervento
function tecniciConOre(r) {
  const durata = Number(r.durataOre) || 0;
  return (r.operatori || []).map((nome, i) => {
    if (!String(nome || "").trim()) return null;
    const o = Number(r.orePerOperatore?.[r.operatoriUid?.[i] || nome]) || durata;
    return { nome: String(nome).trim(), ore: o };
  }).filter(Boolean);
}
const oreIntervento = r => tecniciConOre(r).reduce((t, x) => t + x.ore, 0);
const anomalie = r => [r.anomalieResidui, r.criticita].map(x => String(x || "").trim()).filter(Boolean).join(" — ");
const intervento = r => r.lavorazioni || r.motivoIntervento || r.descrizioneBreve || "";
// Azioni correttive: la soluzione adottata (o proposta) e quello che resta da fare
const azioni = r => [r.soluzionAdottata, r.soluzione, r.attivitaRestanti ? `Da fare: ${r.attivitaRestanti}` : ""].map(x => String(x || "").trim()).filter(Boolean).join(" — ");
const firme = r => [r.firmaTecnico ? "Tecnico" : "", r.firmaCliente ? "Cliente" : ""].filter(Boolean).join(" + ");

// ── Documento stampabile ───────────────────────────────────────────────────
function stampaRegistro({ org, cliente, righe, periodo, commessa, sede, conOre, righeVuote }) {
  const totOre = righe.reduce((t, r) => t + oreIntervento(r), 0);
  const css = `
    @page{size:A4 landscape;margin:12mm 13mm;}
    .foglio{width:297mm;min-height:210mm;padding:12mm 13mm 10mm;}
    .cliente{border:2px solid #7c3aed;background:#faf5ff;border-radius:7px;padding:9px 12px;margin-bottom:12px;display:flex;gap:28px;flex-wrap:wrap;}
    .cliente .t{font-size:8.5px;color:#7c3aed;font-weight:700;text-transform:uppercase;letter-spacing:.05em;}
    .cliente .n{font-size:14px;font-weight:800;color:#4c1d95;}
    .cliente .v{font-size:11px;font-weight:700;color:#1e1b4b;}
    .cliente .s{font-size:10px;color:#4b5563;}
    table{width:100%;border-collapse:collapse;}
    thead{display:table-header-group;}
    tr{page-break-inside:avoid;}
    th{background:#1e1b4b;color:#fff;padding:6px 7px;font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;text-align:left;}
    td{padding:5px 6px;border:1px solid #e5e7eb;font-size:9.5px;vertical-align:top;line-height:1.4;}
    .norma{margin-top:14px;padding:8px 10px;border:1px solid #e5e7eb;border-radius:6px;background:#fafafa;font-size:8.5px;color:#4b5563;line-height:1.5;page-break-inside:avoid;}
    tr:nth-child(even) td{background:#f9fafb;}
    .num{font-family:'Courier New',monospace;font-weight:800;color:#5b21b6;white-space:nowrap;}
    .rosso{color:#b91c1c;}
    .tot{display:flex;gap:24px;justify-content:flex-end;margin-top:8px;font-size:11px;}
    .tot b{font-size:12px;color:#1e1b4b;}
    .firme{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:22px;page-break-inside:avoid;}
    .firma{border-top:1px solid #1e1b4b;padding-top:5px;font-size:9px;color:#6b7280;text-transform:uppercase;font-weight:700;min-height:48px;}`;
  const colonne = ["Data", "Rapportino", "Commessa", "Luogo", "Tipologia", "Intervento eseguito", "Anomalie", "Azioni correttive", "Esito", conOre ? "Eseguito da (ore)" : "Eseguito da", "Firme"];
  const larghezze = ["56px", "84px", "96px", "88px", "58px", "", "112px", "112px", "64px", "104px", "56px"];
  const riga = r => {
    const an = anomalie(r);
    const az = azioni(r);
    const tec = tecniciConOre(r).map(x => `${esc(x.nome)}${conOre && x.ore ? ` (${fmtOre(x.ore)} h)` : ""}`).join("<br/>");
    return `<tr>
      <td style="white-space:nowrap">${dataIt(r.data)}</td>
      <td class="num">${esc(r.numero || "—")}</td>
      <td>${esc((r.codiceCommessa ? r.codiceCommessa + " · " : "") + (r.projectName || "—"))}</td>
      <td>${esc(descrizioneSede(r.sede) || r.sitoOggetto || "—")}</td>
      <td>${esc(TIPOLOGIE[r.tipologiaIntervento] || "—")}</td>
      <td>${esc(tronca(intervento(r), 260) || "—")}</td>
      <td class="${an ? "rosso" : ""}">${esc(tronca(an, 160) || "—")}</td>
      <td>${esc(tronca(az, 160) || "—")}</td>
      <td>${esc(esitoTesto(r.esitoIntervento) || "—")}</td>
      <td>${tec || esc(r.compilatoreRegistro || "—")}</td>
      <td>${esc(firme(r) || "—")}</td>
    </tr>`;
  };
  const vuote = righeVuote ? Array.from({ length: 8 }, () => `<tr>${colonne.map(() => "<td>&nbsp;</td>").join("")}</tr>`).join("") : "";
  const corpo = testataHtml(org, { tipo: "Registro dei controlli e degli interventi", senzaNumero: true, data: `aggiornato al ${new Date().toLocaleDateString("it-IT")}` }) + `
    <div class="cliente">
      <div><div class="t">Cliente</div><div class="n">${esc(cliente.nome || "—")}</div>${cliente.dati ? `<div class="s">${esc(cliente.dati)}</div>` : ""}</div>
      <div><div class="t">Periodo</div><div class="v">${esc(periodo)}</div></div>
      ${commessa ? `<div><div class="t">Commessa</div><div class="v">${esc(commessa)}</div></div>` : ""}
      ${sede ? `<div><div class="t">Luogo</div><div class="v">${esc(sede)}</div></div>` : ""}
      <div><div class="t">Interventi</div><div class="v">${righe.length}</div></div>
    </div>
    <table><thead><tr>${colonne.map((c, i) => `<th${larghezze[i] ? ` style="width:${larghezze[i]}"` : ""}>${c}</th>`).join("")}</tr></thead>
    <tbody>${righe.length ? righe.map(riga).join("") : `<tr><td colspan="${colonne.length}" style="text-align:center;color:#9ca3af;padding:14px">Nessun intervento nel periodo</td></tr>`}${vuote}</tbody></table>
    <div class="tot"><span>Interventi: <b>${righe.length}</b></span>${conOre ? `<span>Ore totali: <b>${fmtOre(totOre)}</b></span>` : ""}</div>
    <div class="firme"><div class="firma">Il responsabile dell'impresa esecutrice</div><div class="firma">Per il committente / datore di lavoro</div></div>
    <div class="norma">Registro dei controlli e degli interventi di manutenzione eseguiti da ${esc(datiNomeImpresa(org))} presso il committente indicato, compilato dai rapportini di intervento numerati, ciascuno firmato dal tecnico e, ove raccolta, dal cliente. Può essere utilizzato dal committente come registrazione dei controlli e delle manutenzioni prevista dalla normativa (D.Lgs. 81/08, art. 71 c. 9 e art. 86 c. 3; per impianti e presidi antincendio, D.M. 1 settembre 2021) e va conservato e tenuto a disposizione degli organi di vigilanza. Le verifiche periodiche degli organismi abilitati (D.P.R. 462/01) sono documentate dai rispettivi verbali.</div>`;
  const piede = piedeHtml(org, [`Registro interventi — ${cliente.nome || ""}`, `stampato il ${new Date().toLocaleDateString("it-IT")}`]);
  apriDocumento({ titolo: `Registro interventi - ${cliente.nome || ""}`, org, css, corpo, piede });
}

// ── Componente ─────────────────────────────────────────────────────────────
// projectId: se presente, registro della sola commessa (scheda della commessa)
export default function RegistroInterventi({ projectId = null }) {
  const { effectiveOrgId, orgVisitata } = useOrg();
  const [rapportini, setRapportini] = useState([]);
  const [progetti, setProgetti] = useState({});
  const [clienti, setClienti] = useState({});
  const [clienteSel, setClienteSel] = useState(null);
  const [cerca, setCerca] = useState("");
  const [anno, setAnno] = useState("");
  const [dal, setDal] = useState("");
  const [al, setAl] = useState("");
  const [commessaSel, setCommessaSel] = useState("");
  const [sedeSel, setSedeSel] = useState("");
  const [conOre, setConOre] = useState(true);
  const [righeVuote, setRigheVuote] = useState(false);

  useEffect(() => {
    if (!effectiveOrgId) return undefined;
    const q = projectId
      ? query(collection(db, "rapportini"), where("projectId", "==", projectId), where("orgId", "==", effectiveOrgId))
      : query(collection(db, "rapportini"), where("orgId", "==", effectiveOrgId));
    return onSnapshot(q, snap => setRapportini(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => setRapportini([]));
  }, [effectiveOrgId, projectId]);

  useEffect(() => {
    if (!effectiveOrgId) return undefined;
    return onSnapshot(query(collection(db, "projects"), where("organizationId", "==", effectiveOrgId)), snap => {
      const m = {}; snap.docs.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; }); setProgetti(m);
    }, () => setProgetti({}));
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return undefined;
    return onSnapshot(query(collection(db, "clienti"), where("organizationId", "==", effectiveOrgId)), snap => {
      const m = {}; snap.docs.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; }); setClienti(m);
    }, () => setClienti({}));
  }, [effectiveOrgId]);

  // Ogni rapportino con il suo cliente (dalla commessa) e i dati aggiornati della commessa
  const righe = useMemo(() => rapportini.map(r => {
    const p = progetti[r.projectId] || null;
    const clienteId = p?.clienteId || (p?.parentId && progetti[p.parentId]?.clienteId) || "";
    const c = clienteId ? clienti[clienteId] : null;
    const nomeCliente = c?.ragioneSociale || c?.nome || p?.clienteNome || r.nomeCliente || "";
    return {
      ...r,
      codiceCommessa: p?.codiceCommessa || r.codiceCommessa || "",
      projectName: p?.name || r.projectName || "",
      chiaveCliente: clienteId ? `id:${clienteId}` : `nome:${normalizza(r.nomeCliente) || "—"}`,
      nomeClienteReg: nomeCliente || "Senza cliente",
    };
  }).sort((a, b) => (a.data || "").localeCompare(b.data || "") || (a.numero || "").localeCompare(b.numero || "")), [rapportini, progetti, clienti]);

  // Elenco dei clienti con interventi
  const elencoClienti = useMemo(() => {
    const m = {};
    righe.forEach(r => {
      const k = r.chiaveCliente;
      if (!m[k]) m[k] = { chiave: k, nome: r.nomeClienteReg, n: 0, ultimo: "", commesse: new Set() };
      m[k].n++; if ((r.data || "") > m[k].ultimo) m[k].ultimo = r.data || "";
      if (r.projectId) m[k].commesse.add(r.projectId);
    });
    return Object.values(m).sort((a, b) => a.nome.localeCompare(b.nome, "it"));
  }, [righe]);

  // Nel registro della commessa il cliente è uno solo: quello della commessa
  const chiaveAttiva = projectId ? (righe[0]?.chiaveCliente || null) : clienteSel;
  const delCliente = useMemo(() => projectId ? righe : righe.filter(r => r.chiaveCliente === chiaveAttiva), [righe, chiaveAttiva, projectId]);

  const anni = useMemo(() => [...new Set(delCliente.map(r => (r.data || "").slice(0, 4)).filter(Boolean))].sort().reverse(), [delCliente]);
  const commesse = useMemo(() => {
    const m = {}; delCliente.forEach(r => { if (r.projectId) m[r.projectId] = (r.codiceCommessa ? r.codiceCommessa + " · " : "") + (r.projectName || ""); });
    return Object.entries(m).sort((a, b) => a[1].localeCompare(b[1], "it"));
  }, [delCliente]);
  const sedi = useMemo(() => [...new Set(delCliente.map(r => descrizioneSede(r.sede) || r.sitoOggetto || "").filter(Boolean))].sort(), [delCliente]);

  const visibili = useMemo(() => delCliente.filter(r => {
    const d = r.data || "";
    if (anno && d.slice(0, 4) !== anno) return false;
    if (dal && d < dal) return false;
    if (al && d > al) return false;
    if (commessaSel && r.projectId !== commessaSel) return false;
    if (sedeSel && (descrizioneSede(r.sede) || r.sitoOggetto || "") !== sedeSel) return false;
    return true;
  }), [delCliente, anno, dal, al, commessaSel, sedeSel]);

  const periodo = anno ? `Anno ${anno}`
    : (dal || al) ? `${dal ? dataIt(dal) : "inizio"} – ${al ? dataIt(al) : "oggi"}`
    : visibili.length ? `${dataIt(visibili[0].data)} – ${dataIt(visibili[visibili.length - 1].data)}` : "—";
  const clienteDoc = (() => {
    const id = chiaveAttiva?.startsWith("id:") ? chiaveAttiva.slice(3) : null;
    const c = id ? clienti[id] : null;
    const nome = delCliente[0]?.nomeClienteReg || c?.ragioneSociale || "";
    const dati = c ? [c.piva ? `P.IVA ${c.piva}` : (c.codiceFiscale ? `C.F. ${c.codiceFiscale}` : ""), c.indirizzo].filter(Boolean).join(" · ") : "";
    return { nome, dati };
  })();
  const totOre = visibili.reduce((t, r) => t + oreIntervento(r), 0);
  const filtriAttivi = anno || dal || al || commessaSel || sedeSel;
  const azzera = () => { setAnno(""); setDal(""); setAl(""); setCommessaSel(""); setSedeSel(""); };

  const stampa = () => stampaRegistro({
    org: orgVisitata || {}, cliente: clienteDoc, righe: visibili, periodo, conOre, righeVuote,
    commessa: commessaSel ? commesse.find(([id]) => id === commessaSel)?.[1] : (projectId ? commesse[0]?.[1] : ""),
    sede: sedeSel,
  });
  const excel = () => esportaXLSX([
    ["Data", "Rapportino", "Commessa", "Luogo", "Tipologia", "Intervento eseguito", "Anomalie", "Azioni correttive", "Esito", "Eseguito da", "Ore", "Firme"],
    ...visibili.map(r => [dataIt(r.data), r.numero || "", (r.codiceCommessa ? r.codiceCommessa + " · " : "") + (r.projectName || ""),
      descrizioneSede(r.sede) || r.sitoOggetto || "", TIPOLOGIE[r.tipologiaIntervento] || "", intervento(r), anomalie(r), azioni(r),
      esitoTesto(r.esitoIntervento), tecniciConOre(r).map(x => x.nome).join(", "), oreIntervento(r), firme(r)]),
  ], `Registro_interventi_${(clienteDoc.nome || "cliente").replace(/[^\w]+/g, "_")}`, "Interventi");

  const inp = { padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none", background: "#fff" };
  const btn = (col, bg) => ({ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", border: `1px solid ${col}`, borderRadius: 8, background: bg, color: col, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif" });

  // ── Elenco dei clienti (solo nel registro generale) ──
  if (!projectId && !clienteSel) {
    const q = normalizza(cerca);
    const lista = elencoClienti.filter(c => !q || normalizza(c.nome).includes(q));
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 24px", flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: "#1e1b4b", margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <FileText size={20} color="#7c3aed"/> Registro Interventi
          </h2>
          <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 12px" }}>Scegli il cliente: trovi tutti gli interventi fatti per lui, in ordine, con data e numero del rapportino.</p>
          <div style={{ position: "relative", maxWidth: 320 }}>
            <Search size={13} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }}/>
            <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Cerca cliente…" style={{ ...inp, paddingLeft: 28, width: "100%", boxSizing: "border-box" }}/>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {lista.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
              <FileText size={44} style={{ opacity: .15, marginBottom: 10 }}/>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{elencoClienti.length ? "Nessun cliente trovato" : "Nessun intervento ancora: il registro si compila da solo dai rapportini"}</div>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
                <tr style={{ background: "#1e1b4b" }}>
                  {["Cliente", "Interventi", "Ultimo intervento", "Commesse"].map(h => <th key={h} style={{ padding: "9px 14px", textAlign: "left", color: "#fff", fontSize: 10, fontWeight: 700 }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {lista.map((c, i) => (
                  <tr key={c.chiave} onClick={() => { setClienteSel(c.chiave); azzera(); }} title="Apri il registro del cliente"
                    style={{ background: i % 2 ? "#fafafa" : "#fff", borderBottom: "1px solid #f3f4f6", cursor: "pointer" }}
                    onMouseOver={e => { e.currentTarget.style.background = "#faf5ff"; }} onMouseOut={e => { e.currentTarget.style.background = i % 2 ? "#fafafa" : "#fff"; }}>
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: "#111827" }}>{c.nome}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: "#5b21b6" }}>{c.n}</td>
                    <td style={{ padding: "10px 14px", color: "#374151" }}>{dataIt(c.ultimo)}</td>
                    <td style={{ padding: "10px 14px", color: "#6b7280" }}>{c.commesse.size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // ── Registro del cliente (o della commessa) ──
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: projectId ? "14px 24px" : "14px 64px 14px 24px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            {!projectId && (
              <button onClick={() => setClienteSel(null)} style={{ display: "flex", alignItems: "center", gap: 3, background: "none", border: "none", color: "#7c3aed", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0, marginBottom: 6, fontFamily: "'Inter',sans-serif" }}>
                <ChevronLeft size={14}/> Tutti i clienti
              </button>
            )}
            <div style={{ fontSize: projectId ? 15 : 18, fontWeight: 800, color: "#1e1b4b", display: "flex", alignItems: "center", gap: 8 }}>
              <FileText size={projectId ? 16 : 20} color="#7c3aed"/> Registro interventi{clienteDoc.nome ? ` — ${clienteDoc.nome}` : ""}
            </div>
            <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 3 }}>
              {clienteDoc.dati ? `${clienteDoc.dati} · ` : ""}{projectId ? "solo gli interventi di questa commessa · " : ""}si aggiorna da solo dai rapportini
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#374151", cursor: "pointer" }} title="Nel documento stampato: le ore di ogni tecnico (i nomi di chi ha eseguito l'intervento ci sono sempre)">
              <input type="checkbox" checked={conOre} onChange={e => setConOre(e.target.checked)} style={{ accentColor: "#7c3aed" }}/> Ore nella stampa
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#374151", cursor: "pointer" }} title="Aggiunge in fondo righe vuote da compilare a mano">
              <input type="checkbox" checked={righeVuote} onChange={e => setRigheVuote(e.target.checked)} style={{ accentColor: "#7c3aed" }}/> Righe vuote
            </label>
            <button onClick={excel} style={btn("#059669", "#f0fdf4")}>📊 Excel</button>
            <button onClick={stampa} style={btn("#7c3aed", "#f5f3ff")}><Printer size={14}/> Stampa / PDF</button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
          <select value={anno} onChange={e => { setAnno(e.target.value); setDal(""); setAl(""); }} style={inp} title="Anno">
            <option value="">Tutti gli anni</option>
            {anni.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <input type="date" value={dal} onChange={e => { setDal(e.target.value); setAnno(""); }} style={inp} title="Dal"/>
          <input type="date" value={al} onChange={e => { setAl(e.target.value); setAnno(""); }} style={inp} title="Al"/>
          {!projectId && commesse.length > 1 && (
            <select value={commessaSel} onChange={e => setCommessaSel(e.target.value)} style={{ ...inp, maxWidth: 260 }} title="Commessa">
              <option value="">Tutte le commesse del cliente</option>
              {commesse.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
            </select>
          )}
          {sedi.length > 1 && (
            <select value={sedeSel} onChange={e => setSedeSel(e.target.value)} style={{ ...inp, maxWidth: 260 }} title="Luogo">
              <option value="">Tutti i luoghi</option>
              {sedi.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {filtriAttivi && <button onClick={azzera} style={{ ...inp, cursor: "pointer" }}>× Azzera filtri</button>}
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#374151" }}>
            <b>{visibili.length}</b> interventi{visibili.length < delCliente.length ? ` (su ${delCliente.length} del cliente: gli altri sono esclusi dai filtri)` : ""} · <b>{fmtOre(totOre)}</b> ore
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {visibili.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
            <FileText size={44} style={{ opacity: .15, marginBottom: 10 }}/>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{delCliente.length ? "Nessun intervento con questi filtri" : "Nessun intervento ancora: il registro si compila da solo dai rapportini"}</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
              <tr style={{ background: "#1e1b4b" }}>
                {["Data", "Rapportino", "Commessa", "Luogo", "Tipologia", "Intervento eseguito", "Anomalie", "Azioni correttive", "Esito", "Eseguito da", "Firme"].map(h => (
                  <th key={h} style={{ padding: "9px 10px", textAlign: "left", color: "#fff", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibili.map((r, i) => {
                const es = STATI_INTERVENTO[r.esitoIntervento];
                const an = anomalie(r);
                return (
                  <tr key={r.id} style={{ background: i % 2 ? "#fafafa" : "#fff", borderBottom: "1px solid #f3f4f6", verticalAlign: "top" }}>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap", fontWeight: 600 }}>{dataIt(r.data)}</td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                      <button onClick={() => apriDocumentoRapportino(r, orgVisitata || {}, r.projectName || "")} title="Apri il rapportino"
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "monospace", fontSize: 12.5, fontWeight: 800, color: "#5b21b6", textDecoration: "underline", textUnderlineOffset: 2 }}>
                        {r.numero || "—"}
                      </button>
                    </td>
                    <td style={{ padding: "8px 10px", color: "#374151", minWidth: 120 }}>{r.codiceCommessa && <span style={{ fontFamily: "monospace", color: "#7c3aed", marginRight: 4 }}>{r.codiceCommessa}</span>}{r.projectName || "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#6b7280", minWidth: 110 }}>{descrizioneSede(r.sede) || r.sitoOggetto || "—"}</td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{TIPOLOGIE[r.tipologiaIntervento] || "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#111827", minWidth: 220, lineHeight: 1.45 }}>{tronca(intervento(r), 220) || "—"}</td>
                    <td style={{ padding: "8px 10px", color: an ? "#b91c1c" : "#9ca3af", minWidth: 140, lineHeight: 1.45 }}>{tronca(an, 140) || "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#374151", minWidth: 140, lineHeight: 1.45 }}>{tronca(azioni(r), 140) || "—"}</td>
                    <td style={{ padding: "8px 10px" }}>{es ? <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: es.bg, color: es.col, fontWeight: 700, whiteSpace: "nowrap" }}>{esitoTesto(r.esitoIntervento)}</span> : "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#374151", whiteSpace: "nowrap", lineHeight: 1.5 }}>{tecniciConOre(r).map(x => <div key={x.nome}>{x.nome}{x.ore ? <span style={{ color: "#6b7280" }}> · {fmtOre(x.ore)} h</span> : null}</div>)}{tecniciConOre(r).length === 0 && "—"}</td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap", color: firme(r) ? "#059669" : "#9ca3af", fontWeight: 600 }}>{firme(r) || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
